import { useEffect, useRef, useState, useCallback } from "react";
import { readCookie, deleteCookie } from "../utils/helpers";

const COOKIE_NAME = "showReportFeedback";

const useReportFeedback = ({
  userGUID,
  assessmentId,
  assessmentAttemptId,
  currentLocale,
  isAuth,
  SubmitSurveyFeedbackConnect,
}) => {
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [showTrustpilotModal, setShowTrustpilotModal] = useState(false);
  const [shouldShowFeedback, setShouldShowFeedback] = useState(false);
  const feedbackSentinelRef = useRef(null);
  const hasTriggered = useRef(false);

  useEffect(() => {
    const cookieValue = readCookie(COOKIE_NAME);
    if (cookieValue === "true" && isAuth) {
      setShouldShowFeedback(true);
    }
  }, [isAuth]);

  useEffect(() => {
    if (!shouldShowFeedback || hasTriggered.current) return;

    const hasScrolled = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      return docHeight > 0 && scrollTop / docHeight >= 0.7;
    };

    const handleScroll = () => {
      if (hasTriggered.current) return;
      if (hasScrolled()) {
        hasTriggered.current = true;
        setShowFeedbackPopup(true);
        window.removeEventListener("scroll", handleScroll);
      }
    };

    const timer = setTimeout(() => {
      window.addEventListener("scroll", handleScroll, { passive: true });
    }, 1000);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [shouldShowFeedback]);

  const handleSurveyFeedback = useCallback(
    async ({ feedback }) => {
      const payload = {
        userId: userGUID,
        feedback,
        assessmentId,
        locale: currentLocale,
        assessmentAttemptId,
      };
      await SubmitSurveyFeedbackConnect(payload);
    },
    [
      userGUID,
      assessmentId,
      currentLocale,
      assessmentAttemptId,
      SubmitSurveyFeedbackConnect,
    ],
  );

  const handleFeedbackClose = useCallback(() => {
    setShowFeedbackPopup(false);
    deleteCookie(COOKIE_NAME);
  }, []);

  const handleFeedbackProceed = useCallback(() => {
    setShowFeedbackPopup(false);
    setShowTrustpilotModal(true);
  }, []);

  const handleTrustpilotClose = useCallback(() => {
    setShowTrustpilotModal(false);
    deleteCookie(COOKIE_NAME);
  }, []);

  return {
    shouldShowFeedback,
    showFeedbackPopup,
    showTrustpilotModal,
    feedbackSentinelRef,
    handleSurveyFeedback,
    handleFeedbackClose,
    handleFeedbackProceed,
    handleTrustpilotClose,
  };
};

export default useReportFeedback;
